import clsx from "clsx";
import React, { useEffect } from "react";
import AdsenseDisplay from "web/react/components/adsense-display/adsense-display";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import { AppFooter } from "web/react/components/app-footer/app-footer";
import Breadcrumbs from "web/react/components/breadcrumbs/breadcrumbs";
import Conditional from "web/react/components/conditional/conditional";
import { BrandAdContainer } from "web/react/components/LEGACY-brand-ad-container/brand-ad-container";
// Components
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import RelatedProductsArea from "web/react/components/LEGACY_related-products-area/related-products-area";
import PDPGallery from "web/react/components/pdp-gallery/pdp-gallery";
import ProductArea from "web/react/components/product-area/product-area";
import ProductsCarousel from "web/react/components/products-carousel/products-carousel";
import RecentlyViewed from "web/react/components/recently-viewed/recently-viewed";
import RelatedDesignersRail from "web/react/components/related-designers/related-designers-rail";
import RelatedProductsSidebar from "web/react/components/related-products-sidebar/related-products-sidebar";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
// Hooks
import { useProductSelector } from "web/react/hooks/use-product-selector/use-product-selector";
import { useSFLPrefetch } from "web/react/hooks/use-save-for-later/use-save-for-later";
// Styles
import layout from "web/react/pages/product/in-stock/LEGACY/in-stock-layout.module.css";
import { gettext } from "web/script/modules/django-i18n";
import userProfiler from "web/script/modules/userprofiler";
import {
    InStockIconProductLayoutSerializer,
    ProductAreaSerializer,
    ProductSerializer,
} from "web/types/serializers";

interface InStockExpressCheckoutProductPageProps {
    productArea: ProductAreaSerializer;
    productLayout: InStockIconProductLayoutSerializer;
    helpCentreURLs: string[];
    showCookieSettingsOption: boolean;
    country: string;
}

function InStockExpressCheckoutProductPage({
    productArea,
    productLayout,
    helpCentreURLs,
    showCookieSettingsOption,
    country,
}: InStockExpressCheckoutProductPageProps): React.ReactElement {
    const {
        product_analytics_data: productAnalyticsData,
        related_products_sidebar: relatedProductsSidebar,
        product_edit_button: productEditButton,
        adsense_for_shopping: adsenseForShopping,
        adsense_for_display: adsenseForDisplay,
        brand_ad: brandAd,
        retailer_related_products_gallery: retailerRelatedProductsGallery,
        related_products: relatedProducts,
        related_designers_category_rail: relatedDesignersCategoryRail,
        related_search_rail: relatedSearchRail,
        product_breadcrumbs: productBreadcrumbs,
        recently_viewed: recentlyViewedProducts,
        related_from_retailer: relatedFromRetailer,
        retailer_slug: retailerSlug,
        footer,
        features,
    } = productLayout;

    useSFLPrefetch(productArea.sorted_variants as string[]);

    const relatedGalleryRelatedProducts =
        retailerRelatedProductsGallery && retailerRelatedProductsGallery.length > 0
            ? retailerRelatedProductsGallery.slice(0, 4)
            : [];

    const [currentProduct, setCurrentProduct] = useProductSelector(
        productArea.sorted_variants as string[]
    );

    const product: ProductSerializer = productArea.products[currentProduct];

    useEffect(() => {
        userProfiler.saveSeenProduct(productAnalyticsData);
    }, [productAnalyticsData]);

    return (
        <div className={layout.container}>
            <CustomerCaptureOverlay />

            <div className={layout.primary}>
                <article
                    className={clsx(layout.info, {
                        [layout.infoWithSidebar]: !!relatedProductsSidebar,
                    })}
                >
                    <div className={layout.gallery}>
                        <Conditional
                            check={productEditButton !== null && productEditButton.is_user_staff}
                            key="edit-button"
                        >
                            <a
                                href={`https://os.lystit.com/product-data/product/${productEditButton.product_slug}/${productEditButton.product_id}/edit`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={layout.edit}
                            >
                                <button className="button black">
                                    {gettext("product_edit_button.label")}
                                </button>
                            </a>
                        </Conditional>

                        <PDPGallery
                            product={product}
                            relatedProducts={relatedGalleryRelatedProducts}
                            hasRelatedProductsSidebar={!!relatedProductsSidebar}
                        />
                    </div>

                    <div className={layout.details}>
                        <ProductArea
                            productArea={productArea}
                            handleProductChange={(id) => setCurrentProduct(id)}
                            selectedVariant={currentProduct}
                        />
                    </div>
                </article>

                {relatedProductsSidebar && (
                    <RelatedProductsSidebar
                        relatedProductsSidebar={relatedProductsSidebar}
                        variant="vertical"
                        className={clsx(layout.sidebar, "affiliateSidebar")}
                    />
                )}
            </div>

            <div className={layout.secondary}>
                {relatedFromRetailer && (
                    <ProductsCarousel
                        title={relatedFromRetailer.title}
                        products={relatedFromRetailer.products}
                    />
                )}

                {adsenseForShopping && <AdsenseShopping {...adsenseForShopping} />}

                {brandAd && <BrandAdContainer {...brandAd} />}

                {relatedProducts && (
                    <RelatedProductsArea
                        sections={relatedProducts.sections}
                        isAffiliate={false}
                        retailerSlug={retailerSlug}
                    />
                )}

                {adsenseForDisplay && (
                    <AdsenseDisplay
                        divId={adsenseForDisplay.div_id}
                        slotId={adsenseForDisplay.slot_id}
                        usesMobileSize={adsenseForDisplay.uses_mobile_size}
                    />
                )}
            </div>

            {relatedDesignersCategoryRail && !!relatedDesignersCategoryRail.designers.length && (
                <RelatedDesignersRail
                    title={relatedDesignersCategoryRail.title}
                    designers={relatedDesignersCategoryRail.designers}
                    features={features}
                    showImages={false}
                />
            )}

            {relatedSearchRail && !!relatedSearchRail.related_searches.length && (
                <RelatedSearchesRail
                    title={gettext("product.related_products.related_searches")}
                    relatedSearches={relatedSearchRail.related_searches}
                    features={features as any}
                />
            )}

            {productBreadcrumbs && (
                <div className={layout.secondary}>
                    <Breadcrumbs breadcrumbs={productBreadcrumbs.breadcrumbs} type="pdp" />
                </div>
            )}

            <div className={layout.secondary}>
                {recentlyViewedProducts && (
                    <RecentlyViewed
                        title={recentlyViewedProducts.title}
                        products={recentlyViewedProducts.products}
                        is_loading={recentlyViewedProducts.is_loading}
                        current_product_id={recentlyViewedProducts.current_product_id}
                        in_overlay={recentlyViewedProducts.in_overlay}
                        isRedesign={false}
                    />
                )}
            </div>

            <AppFooter
                data={footer}
                helpCenterUrls={helpCentreURLs}
                showCookieSettingsOption={showCookieSettingsOption}
                country={country}
            />
        </div>
    );
}

export default InStockExpressCheckoutProductPage;
